/**
 * Device Configuration API
 *
 * Manages hospital device discovery and configuration via the printAgent gateway prefix.
 * - POST /printer/discover  - discover available printers
 * - POST /scanner/discover  - discover available scanners
 * - GET  /devices           - list configured hospital devices
 * - POST /devices           - add a device to hospital configuration
 * - DELETE /devices/:id     - remove a device from hospital configuration
 */
import { getApiClient } from '@/core/services/api.service';
import { config, API_ROUTES } from '@/core/config';
import type { DeviceApiRecord } from '@/shared/composables/usePrinterDiscovery';
import type {
  ScannerScanRequest,
  AddDeviceInput,
} from '@tms-phoenix/tms-types';
 
export type { ScannerScanMode, ScannerScanRequest, AddDeviceInput } from '@tms-phoenix/tms-types';
 
/**
 * Build the full absolute SSE URL for the scanner events stream.
 * Uses the app base URL so EventSource can open an absolute URL.
 * Tenant scope is resolved server-side from the caller's authenticated
 * session (ScannerController.streamEvents) — never sent as a query param.
 */
export function buildScannerEventsUrl(): string {
  const base = config.api.baseUrl.replace(/\/$/, '');
  return `${base}${API_ROUTES.PRINT_AGENT}/scanner/events`;
}
 
/** POST a scan request to the scanner agent. */
export async function requestScannerScan(payload: ScannerScanRequest) {
  const api = getApiClient();
  return api.post(`${API_ROUTES.PRINT_AGENT}/scanner/scan`, payload);
}
 
/** Get only the scanner-type devices from the configured device list. */
export async function getConfiguredScannerDevices(): Promise<DeviceApiRecord[]> {
  const devices = await getConfiguredDevices();
  return devices.filter(
    (d) => (d.device_type ?? d.deviceType)?.toLowerCase() === 'scanner',
  );
}
 
interface DiscoveredDeviceRaw {
  name: string;
  ipAddress?: string | null;
  isNetworkPrinter?: boolean;
  status?: string;
  serialNumber?: string | null;
  model?: string | null;
  locationDescription?: string | null;
  macAddress?: string | null;
  careUnitId?: string | null;
  printerPort?: number | null;
  dpi?: number | null;
  dpiSource?: string | null;
}
 
interface DiscoveryResponse<T> {
  success: boolean;
  status?: string;
  agentId?: string;
  devices: T[];
  processingTimeMs?: number;
  timestamp?: string;
}
 
function toDeviceRecord(params: {
  agentId: string;
  hospitalId: string;
  deviceType: 'printer' | 'scanner';
  deviceName: string;
  macAddress?: string | null;
  model?: string | null;
  serialNumber?: string | null;
  locationDescription?: string | null;
  careUnitId?: string | null;
  status?: string;
  printerPort?: number | null;
  dpi?: number | null;
  dpiSource?: string | null;
}): DeviceApiRecord {
  const nowIso = new Date().toISOString();
  return {
    // snake_case shape (primary backend shape)
    agent_id: params.agentId,
    hospital_id: params.hospitalId,
    device_type: params.deviceType,
    device_name: params.deviceName,
    mac_address: params.macAddress ?? null,
    model: params.model ?? null,
    serial_number: params.serialNumber ?? null,
    location_description: params.locationDescription ?? null,
    care_unit_id: params.careUnitId ?? null,
    status: params.status || 'Online',
    discovered_at: nowIso,
    // camelCase compatibility fields
    agentId: params.agentId,
    hospitalId: params.hospitalId,
    deviceType: params.deviceType,
    deviceName: params.deviceName,
    macAddress: params.macAddress ?? '',
    serialNumber: params.serialNumber ?? null,
    locationDescription: params.locationDescription ?? null,
    careUnitId: params.careUnitId ?? null,
    discoveredAt: nowIso,
    printerPort: params.printerPort ?? null,
    dpi: params.dpi ?? null,
    dpiSource: params.dpiSource ?? null,
  } as DeviceApiRecord;
}
 
function extractDiscoveryBody<T>(payload: unknown): DiscoveryResponse<T> {
  if (payload && typeof payload === 'object') {
    const obj = payload as Record<string, unknown>;
    if (Array.isArray(obj.devices)) {
      return {
        success: Boolean(obj.success),
        status: typeof obj.status === 'string' ? obj.status : undefined,
        agentId: typeof obj.agentId === 'string' ? obj.agentId : undefined,
        devices: obj.devices as T[],
        processingTimeMs: typeof obj.processingTimeMs === 'number' ? obj.processingTimeMs : undefined,
        timestamp: typeof obj.timestamp === 'string' ? obj.timestamp : undefined,
      };
    }
    if (obj.data && typeof obj.data === 'object') {
      const data = obj.data as Record<string, unknown>;
      if (Array.isArray(data.devices)) {
        return {
          success: Boolean(data.success),
          status: typeof data.status === 'string' ? data.status : undefined,
          agentId: typeof data.agentId === 'string' ? data.agentId : undefined,
          devices: data.devices as T[],
          processingTimeMs: typeof data.processingTimeMs === 'number' ? data.processingTimeMs : undefined,
          timestamp: typeof data.timestamp === 'string' ? data.timestamp : undefined,
        };
      }
    }
  }
  return { success: false, devices: [] } as DiscoveryResponse<T>;
}
 
/** Discover available printers on the network. */
export async function discoverPrinters(): Promise<DeviceApiRecord[]> {
  const api = getApiClient();
  const hospitalId = config.hospitalId?.trim() || '';
  const resp = await api.post<unknown>(`${API_ROUTES.PRINT_AGENT}/printer/discover`, {
    deviceType: 'printer',
    hospitalId,
  });
 
  const body = extractDiscoveryBody<DiscoveredDeviceRaw>(resp.data);
  if (!Array.isArray(body.devices) || body.devices.length === 0) return [];
  if (!body.agentId) {
    throw new Error('Discovery response missing agentId - devices cannot be registered.');
  }
 
  return body.devices.map((d) =>
    toDeviceRecord({
      agentId: body.agentId as string,
      hospitalId,
      deviceType: 'printer',
      deviceName: d.name,
      macAddress: d.macAddress ?? '',
      model: d.model ?? null,
      serialNumber: d.serialNumber ?? null,
      locationDescription: d.locationDescription ?? d.ipAddress ?? null,
      careUnitId: d.careUnitId ?? null,
      status: d.status || 'Online',
      printerPort: d.printerPort ?? null,
      dpi: d.dpi ?? null,
      dpiSource: d.dpiSource ?? null,
    }),
  );
}
 
/**
 * Get all devices configured for the hospital.
 * Handles both already-unwrapped arrays and full envelopes.
 */
export async function getConfiguredDevices(): Promise<DeviceApiRecord[]> {
  const api = getApiClient();
  const resp = await api.get<unknown>(`${API_ROUTES.PRINT_AGENT}/devices`);
 
  const payload = resp.data;
  if (Array.isArray(payload)) return payload as DeviceApiRecord[];
 
  if (payload && typeof payload === 'object') {
    const envelope = payload as Record<string, unknown>;
    if (Array.isArray(envelope.data)) return envelope.data as DeviceApiRecord[];
  }
 
  return [];
}
 
// AddDeviceInput is defined in @tms-phoenix/tms-types and re-exported above.
 
/**
 * Add a discovered device to hospital configuration (POST /devices).
 */
export async function addConfiguredDevice(input: AddDeviceInput): Promise<DeviceApiRecord> {
  const api = getApiClient();
  const hospitalId = config.hospitalId?.trim() || '';
  const resp = await api.post<DeviceApiRecord>(`${API_ROUTES.PRINT_AGENT}/devices`, {
    agentId: input.agentId,
    hospitalId,
    deviceType: input.deviceType,
    deviceName: input.deviceName,
    macAddress: input.macAddress ?? null,
    model: input.model ?? null,
    serialNumber: input.serialNumber ?? null,
    locationDescription: input.locationDescription ?? null,
    status: input.status || 'DISCOVERED',
    discoveredAt: input.discoveredAt ?? null,
    printerPort: input.printerPort ?? null,
    dpi: input.dpi ?? null,
    dpiSource: input.dpiSource ?? null,
  });
  return resp.data;
}
 
/** Remove a device from hospital configuration. */
export async function removeConfiguredDevice(deviceId: string): Promise<void> {
  const api = getApiClient();
  await api.delete(`${API_ROUTES.PRINT_AGENT}/devices/${deviceId}`);
}
 
/** Clear care_unit_id on a device (de-assign from unit). */
export async function clearDeviceCareUnit(deviceId: string): Promise<void> {
  const api = getApiClient();
  await api.patch(`${API_ROUTES.PRINT_AGENT}/devices/${deviceId}`, {
    careUnitId: null,
  });
}
 
/**
 * Download the full gRPC certificate bundle via the gateway API.
 * Kept for backward compat - use downloadCertificateFile(cert) for individual files.
 */
export async function downloadMtlsCertificate(): Promise<void> {
  const api = getApiClient();
  const resp = await api.get<Blob>(`${API_ROUTES.PRINT_AGENT}/downloads/grpc-certs`, {
    responseType: 'blob',
  });
  triggerBlobDownload(resp.data, 'tms-grpc-certs.zip');
}
 
export interface PrintAgentCertificate {
  fileName: string;
  tenantId: string | null;
  status: string;
  expiryDate: string | null;
  downloadUrl: string | null;
}
 
export interface PrintAgentConnectionStatus {
  connected: boolean;
  connectedAgents?: string[];
  agentVersion?: string;
  lastHeartbeat?: string;
  connectionState?: 'DISCONNECTED' | 'PARTIAL' | 'CONNECTED';
  statusText?: string;
  partial?: boolean;
  channels?: Record<string, unknown>;
  error?: string;
}
 
function triggerBlobDownload(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  setTimeout(() => {
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, 0);
}
 
function triggerUrlDownload(url: string, filename?: string): void {
  const a = document.createElement('a');
  a.href = url;
  if (filename) a.download = filename;
  a.target = '_blank';
  a.rel = 'noopener noreferrer';
  document.body.appendChild(a);
  a.click();
  setTimeout(() => document.body.removeChild(a), 0);
}
 
/**
 * List available gRPC certificates with metadata.
 */
export async function getPrintAgentCertificates(): Promise<PrintAgentCertificate[]> {
  const api = getApiClient();
  const resp = await api.get<unknown>(`${API_ROUTES.PRINT_AGENT}/downloads/grpc-certs/files`);
  const payload = resp.data;
 
  if (Array.isArray(payload)) return payload as PrintAgentCertificate[];
  if (payload && typeof payload === 'object') {
    const p = payload as Record<string, unknown>;
    if (Array.isArray(p.data)) return p.data as PrintAgentCertificate[];
  }
  return [];
}
 
/**
 * Download an individual gRPC certificate using its pre-signed downloadUrl.
 * Fails fast with an error if downloadUrl is not available.
 */
export async function downloadCertificateFile(cert: PrintAgentCertificate): Promise<void> {
  if (!cert.downloadUrl) {
    throw new Error(
      `Certificate "${cert.fileName}" is not available for download. The download URL has not been generated yet — ` +
      `please try again in a moment or contact support if the issue persists.`
    );
  }
  triggerUrlDownload(cert.downloadUrl, cert.fileName);
}
 
/**
 * Check whether the gRPC PrintAgent is connected and available.
 */
export async function checkPrintAgentConnection(): Promise<PrintAgentConnectionStatus> {
  const api = getApiClient();
  const resp = await api.get<unknown>(`${API_ROUTES.PRINT_AGENT}/downloads/connectivity`);
  const payload = resp.data;
 
  if (!payload || typeof payload !== 'object') {
    return { connected: false, error: 'Invalid response from PrintAgent' };
  }
 
  const p = payload as Record<string, unknown>;
  const data = ('connected' in p ? p : (p.data as Record<string, unknown> | undefined) ?? p);
 
  const agentsRaw = Array.isArray(data.agents) ? data.agents : [];
  const connectedAgents = agentsRaw
    .map((a: unknown): string => {
      if (a && typeof a === 'object') {
        const agent = a as Record<string, unknown>;
        return typeof agent.agentName === 'string' ? agent.agentName : String(agent.agentId ?? '');
      }
      return String(a);
    })
    .filter(Boolean);
 
  const connectionState =
    typeof data.connectionState === 'string'
      ? (data.connectionState as PrintAgentConnectionStatus['connectionState'])
      : undefined;
  const statusText = typeof data.statusText === 'string' ? data.statusText : undefined;
  const partial = typeof data.partial === 'boolean' ? data.partial : undefined;
  const channels = data.channels && typeof data.channels === 'object'
    ? (data.channels as Record<string, unknown>)
    : undefined;
 
  return {
    connected: !!data.connected,
    connectedAgents,
    ...(connectionState ? { connectionState } : {}),
    ...(statusText ? { statusText } : {}),
    ...(typeof partial === 'boolean' ? { partial } : {}),
    ...(channels ? { channels } : {}),
  };
}
 
/**
 * Fetch the gRPC public URL from the /downloads/grpc-certs/files endpoint.
 * The shared axios response interceptor unwraps `response.data` to the inner
 * `data` array and preserves sibling envelope fields (like `grpc_url`) as
 * extra properties on the response object — see packages/tms-vue/src/composables/useApi.ts.
 */
export async function getGrpcUrl(): Promise<string | null> {
  const api = getApiClient();
  const resp = await api.get<unknown>(`${API_ROUTES.PRINT_AGENT}/downloads/grpc-certs/files`);
  const grpcUrl = (resp as unknown as { grpc_url?: unknown }).grpc_url;
  return typeof grpcUrl === 'string' && grpcUrl ? grpcUrl : null;
}

/**
 * Fetch the tenant's allowed IP/CIDR ranges from the /downloads/grpc-certs/files endpoint.
 * Mirrors `getGrpcUrl` — reads the sibling `ip_ranges` envelope field preserved by the
 * shared axios response interceptor. Same values as the BridgeLink assets endpoint.
 */
export async function getGrpcIpRanges(): Promise<string[]> {
  const api = getApiClient();
  const resp = await api.get<unknown>(`${API_ROUTES.PRINT_AGENT}/downloads/grpc-certs/files`);
  const ipRanges = (resp as unknown as { ip_ranges?: unknown }).ip_ranges;
  return Array.isArray(ipRanges) ? ipRanges.filter((r): r is string => typeof r === 'string') : [];
}

interface PrintAgentInstallerInfo {
  fileName?: string;
  downloadUrl?: string;
  userGuideUrl?: string | null;
  userGuideFileName?: string | null;
}
 
/**
 * Download the PrintAgent installer via backend metadata response.
 * The endpoint returns JSON with a signed S3 URL, not a binary blob.
 */
export async function downloadPrintAgentInstaller(): Promise<void> {
  const api = getApiClient();
  const resp = await api.get<unknown>(`${API_ROUTES.PRINT_AGENT}/downloads/print-agent`);
 
  const payload = resp.data;
  let info: PrintAgentInstallerInfo | null = null;
 
  if (payload && typeof payload === 'object') {
    const p = payload as Record<string, unknown>;
    if (typeof p.downloadUrl === 'string') {
      info = p as PrintAgentInstallerInfo;
    } else if (p.data && typeof p.data === 'object') {
      info = p.data as PrintAgentInstallerInfo;
    }
  }
 
  if (!info?.downloadUrl) {
    throw new Error('PrintAgent download URL was not returned by the server.');
  }
 
  triggerUrlDownload(info.downloadUrl, info.fileName || 'PrintAgent.zip');
}
 
/**
 * Download the PrintAgent user guide via the same backend metadata endpoint
 * used for the installer. Uses the `userGuideUrl` pre-signed S3 URL from the
 * response. Throws if the user guide URL is not available.
 */
export async function downloadPrintAgentUserGuide(): Promise<void> {
  const api = getApiClient();
  const resp = await api.get<unknown>(`${API_ROUTES.PRINT_AGENT}/downloads/print-agent`);

  const payload = resp.data;
  let info: PrintAgentInstallerInfo | null = null;

  if (payload && typeof payload === 'object') {
    const p = payload as Record<string, unknown>;
    if (typeof p.userGuideUrl === 'string' || p.userGuideUrl === null) {
      info = p as PrintAgentInstallerInfo;
    } else if (p.data && typeof p.data === 'object') {
      info = p.data as PrintAgentInstallerInfo;
    }
  }

  if (!info?.userGuideUrl) {
    const err = new Error('User guide is not available');
    (err as Error & { code: string }).code = 'USER_GUIDE_NOT_AVAILABLE';
    throw err;
  }

  triggerUrlDownload(info.userGuideUrl, info.userGuideFileName || 'TMS-Printing.pdf');
}